import { useState, useEffect, useMemo } from 'react';
import { legacyApi, LegacySupplier } from '../../../services/legacyApi';
import { Plus, Trash2, Pencil, RotateCw } from 'lucide-react';
import { SupplierForm } from './SupplierForm';
import { Button } from '../../../components/ui/button';
import { CompactTable } from '../../../components/ui/CompactTable';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { includesNormalized, normalizeText } from '../../../components/table-filters/filterPredicates';
import { useTableFilterState } from '../../../hooks/useTableFilterState';

export function LegacySuppliers() {
    const [suppliers, setSuppliers] = useState<LegacySupplier[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState<LegacySupplier | null>(null);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        emirate: 'all',
        has_trn: 'all'
    });

    useEffect(() => {
        loadSuppliers();
    }, []);

    const emirateOptions = useMemo(
        () => Array.from(new Set(suppliers.map((s) => normalizeText(s.emirate)).filter(Boolean))).sort(),
        [suppliers]
    );

    const filteredSuppliers = useMemo(() => {
        return suppliers.filter((s) => {
            const matchesSearch = includesNormalized(
                [
                    s.supplier_name,
                    s.poc_name,
                    s.poc_contact,
                    s.email_address,
                    s.office_tel,
                    s.trn
                ].join(' '),
                state.q
            );
            const matchesEmirate = state.emirate === 'all' || normalizeText(s.emirate) === state.emirate;
            const hasTrn = normalizeText(s.trn).length > 0;
            const matchesTrn =
                state.has_trn === 'all' ||
                (state.has_trn === 'yes' && hasTrn) ||
                (state.has_trn === 'no' && !hasTrn);
            return matchesSearch && matchesEmirate && matchesTrn;
        });
    }, [suppliers, state.q, state.emirate, state.has_trn]);

    const loadSuppliers = () => {
        setLoading(true);
        legacyApi.suppliers.list()
            .then(data => {
                setSuppliers(data);
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const { confirm } = useConfirm();

    const handleDelete = async (id: number) => {
        const ok = await confirm('Are you sure you want to delete this supplier?', { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            await legacyApi.suppliers.delete(id);
            const remaining = suppliers.filter(s => s.supplier_id !== id);
            setSuppliers(remaining); // Filter will auto-update via effect
            toast.success('Supplier deleted');
        } catch (e) {
            toast.error('Failed to delete');
        }
    };

    const columns = [
        { header: "ID", accessor: (s: LegacySupplier) => `SUP-${s.supplier_id}`, className: 'font-mono whitespace-nowrap w-24' },
        { header: "Supplier Name", accessor: (s: LegacySupplier) => s.supplier_name, className: 'font-medium min-w-[200px] max-w-[300px] truncate' },
        { header: "POC Name", accessor: (s: LegacySupplier) => s.poc_name || '-', className: 'min-w-[120px] max-w-[200px] truncate' },
        { header: "Contact", accessor: (s: LegacySupplier) => s.poc_contact || '-', className: 'whitespace-nowrap' },
        { header: "Emirate", accessor: (s: LegacySupplier) => s.emirate || '-', className: 'w-32 truncate' },
        { header: "Office Tel", accessor: (s: LegacySupplier) => s.office_tel || '-', className: 'whitespace-nowrap' },
        { header: "Email", accessor: (s: LegacySupplier) => s.email_address || '-', className: 'min-w-[150px] max-w-[250px] truncate' },
        { header: "TRN", accessor: (s: LegacySupplier) => s.trn || '-', className: 'font-mono w-32 truncate' },
        {
            header: "Actions",
            accessor: (s: LegacySupplier) => (
                <div className="flex gap-1 justify-end">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); setEditingSupplier(s); setIsModalOpen(true); }}
                        title="Edit Supplier"
                    >
                        <Pencil className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); handleDelete(s.supplier_id); }}
                        title="Delete Supplier"
                    >
                        <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                </div>
            ),
            className: 'text-right'
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Suppliers</h1>
                    <p className="text-muted-foreground">Manage supplier records</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={loadSuppliers}>
                        <RotateCw className="mr-2 h-4 w-4" /> Refresh
                    </Button>
                    <Button onClick={() => { setEditingSupplier(null); setIsModalOpen(true); }}>
                        <Plus className="mr-2 h-4 w-4" /> Add Supplier
                    </Button>
                </div>
            </div>

            <div className="space-y-4">
                <TableFilterBar
                    searchValue={state.q}
                    onSearchChange={(value) => setFilter('q', value)}
                    searchPlaceholder="Search suppliers by name, POC, email, TRN..."
                    controls={[
                        {
                            key: 'emirate',
                            label: 'Emirate',
                            type: 'select',
                            value: state.emirate,
                            onChange: (value) => setFilter('emirate', value),
                            options: [
                                { value: 'all', label: 'All Emirates' },
                                ...emirateOptions.map((option) => ({ value: option, label: option }))
                            ]
                        },
                        {
                            key: 'has_trn',
                            label: 'TRN',
                            type: 'select',
                            value: state.has_trn,
                            onChange: (value) => setFilter('has_trn', value),
                            options: [
                                { value: 'all', label: 'All TRN States' },
                                { value: 'yes', label: 'With TRN' },
                                { value: 'no', label: 'Without TRN' }
                            ]
                        }
                    ]}
                    isDirty={isDirty}
                    onClear={clearAll}
                    rightSlot={`${filteredSuppliers.length} of ${suppliers.length}`}
                />

                <div className="min-h-[400px] overflow-x-auto">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading Suppliers...</div>
                    ) : (
                        <CompactTable
                            data={filteredSuppliers}
                            columns={columns}
                            keyField="supplier_id"
                        />
                    )}
                </div>
            </div>

            {isModalOpen && (
                <SupplierForm
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={() => { setIsModalOpen(false); loadSuppliers(); }}
                    initialData={editingSupplier}
                />
            )}
        </div>
    );
}
